import React, { useMemo, useState } from "react";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";
import { Alert, Form } from "react-bootstrap";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import AppButton from "../layout/AppButton";

import { getToken } from "../auth/authConfig";
import { apiService } from "../../services/apiClient";
import { apiUrl } from "../../services/apiUrl";
import ToastNotification from "../layout/ToastNotification";

const filterKeyOf = (filter) => `${filter?.target?.table}.${filter?.target?.column}`;

/** Distinct values of the first column of an exportData() CSV. */
function parseExportedColumn(csv) {
  if (!csv) return [];
  const rows = csv.trim().split(/\r?\n/).slice(1);
  const values = rows
    .map((row) => row.split(",")[0].replace(/^"|"$/g, "").trim())
    .filter(Boolean);
  return [...new Set(values)];
}

/**
 * AddVisualsToReportDialog - Add visuals to a specific report
 * @param {Object} report - Report object with id, reportId, reportName, groupId
 */
const AddVisualsToReportDialog = ({ report }) => {
  const [open, setOpen] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportConfig, setReportConfig] = useState(null);
  const [pages, setPages] = useState([]);
  const [selectedPageName, setSelectedPageName] = useState("");
  const [availableVisuals, setAvailableVisuals] = useState([]);
  const [selectedVisualNames, setSelectedVisualNames] = useState([]);
  const [cart, setCart] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedVisuals, setSavedVisuals] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const showToast = (msg, severity = "success") => setToast({ open: true, message: msg, severity });

  const defaultCount = useMemo(
    () => cart.filter((item) => item.is_default === "YES").length,
    [cart]
  );

  const selectedPageDisplayName = useMemo(() => {
    const found = pages.find((item) => item.name === selectedPageName);
    return found ? found.displayName : "";
  }, [pages, selectedPageName]);

  const resetState = () => {
    setLoadingReport(false);
    setReportConfig(null);
    setPages([]);
    setSelectedPageName("");
    setAvailableVisuals([]);
    setSelectedVisualNames([]);
    setCart([]);
    setSaving(false);
    setSavedVisuals([]);
    setMessage({ type: "", text: "" });
  };

  const openDialog = async () => {
    setOpen(true);
    setMessage({ type: "", text: "" });

    if (reportConfig) {
      return;
    }

    setLoadingReport(true);
    try {
      // Fetch existing visuals for this report
      const visualsRes = await apiService.get(apiUrl.aipdPersonaVisuals());
      const allVisuals = Array.isArray(visualsRes.data?.all) ? visualsRes.data.all : [];
      // Filter visuals that belong to this report (no user_group_id = report-level visuals)
      const reportVisuals = allVisuals.filter(v => 
        String(v.reportId) === String(report.id) && !v.userGroupId
      );
      setSavedVisuals(reportVisuals.map(v => ({
        visual_name: v.visualName,
        title: v.title,
        report_name: v.reportName,
        filters: Array.isArray(v.filters) ? v.filters : [],
      })));

      // Load the report embed config
      const token = await getToken();
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/aipd/reports/${report.id}`, {
        method: "GET",
        headers: {
          "oidc-token": token,
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Unable to load report");
      }

      const data = await response.json();
      setReportConfig(data?.data || null);
    } catch (error) {
      setMessage({ type: "danger", text: "Unable to load report." });
    } finally {
      setLoadingReport(false);
    }
  };

  const closeDialog = () => {
    setOpen(false);
    resetState();
  };

  const loadVisualsForPage = async (pageName, embeddedReportRef) => {
    try {
      if (!pageName || !embeddedReportRef) {
        return;
      }

      const pageList = await embeddedReportRef.getPages();
      const page = pageList.find((item) => item.name === pageName);
      if (!page) {
        setAvailableVisuals([]);
        setSelectedVisualNames([]);
        return;
      }

      const visuals = await page.getVisuals();
      const mappedVisuals = visuals
        .filter((visual) => visual && visual.name)
        .map((visual) => ({
          id: visual.name,
          title: visual.title || visual.displayName || visual.name,
          type: visual.type || "visual",
        }));

      setAvailableVisuals(mappedVisuals);

      // Pre-check visuals already saved for this page
      const preChecked = mappedVisuals
        .filter((v) => savedVisuals.some((s) => s.visual_name === v.id && s.report_name === pageName))
        .map((v) => v.id);
      setSelectedVisualNames(preChecked);
    } catch (error) {
      setMessage({ type: "danger", text: "Unable to load visuals for this page." });
      setAvailableVisuals([]);
      setSelectedVisualNames([]);
    }
  };

  const handleEmbeddedReport = (embeddedReport) => {
    window.reportVisualReport = embeddedReport;

    embeddedReport.on("loaded", async () => {
      try {
        const allPages = await embeddedReport.getPages();
        const visiblePages = allPages.filter((page) => page.visibility !== 1);
        const mappedPages = visiblePages.map((page) => ({
          name: page.name,
          displayName: page.displayName || page.name,
          isActive: page.isActive,
        }));
        setPages(mappedPages);

        const activePage = mappedPages.find((page) => page.isActive) || mappedPages[0];
        if (activePage) {
          setSelectedPageName(activePage.name);
          await loadVisualsForPage(activePage.name, embeddedReport);
        }
      } catch (error) {
        setPages([]);
      }
    });

    embeddedReport.on("pageChanged", async (event) => {
      const page = event?.detail?.newPage;
      if (!page) {
        return;
      }

      setSelectedPageName(page.name);
      await loadVisualsForPage(page.name, embeddedReport);
    });
  };

  const handlePageChange = async (event) => {
    const nextPageName = event.target.value;
    setSelectedPageName(nextPageName);

    try {
      const reportRef = window.reportVisualReport;
      if (reportRef && nextPageName) {
        const pageList = await reportRef.getPages();
        const page = pageList.find((item) => item.name === nextPageName);
        if (page) {
          await page.setActive();
        }
      }
    } catch (error) {
      // Keep flow alive even if explicit setActive fails.
    }

    await loadVisualsForPage(nextPageName, window.reportVisualReport);
  };

  const toggleVisual = (visualId) => {
    setSelectedVisualNames((prev) =>
      prev.includes(visualId)
        ? prev.filter((item) => item !== visualId)
        : [...prev, visualId]
    );
  };

  /**
   * Snapshot the slice the admin has set up in the embedded report, plus the
   * values a viewer will be able to choose from.
   */
  const captureCurrentFilters = async (visualIds = []) => {
    const reportRef = window.reportVisualReport;
    if (!reportRef) {
      console.warn("[AddVisuals] no embedded report reference yet");
      return { filters: [], options: {} };
    }

    const isUsable = (filter) =>
      filter &&
      filter.filterType === models.FilterType.Basic &&
      filter.operator === "In" &&
      filter.target?.table &&
      filter.target?.column &&
      Array.isArray(filter.values) &&
      filter.values.length > 0;

    const collected = [];
    const options = {};
    const diagnostics = { report: 0, page: 0, slicers: 0, visual: 0, slicerCount: 0 };

    const SCOPE = { report: 1, page: 2, visual: 3, slicer: 4 };
    const collect = (scope, filters) => collected.push(...filters.map((filter) => ({ scope, filter })));

    try {
      const reportFilters = (await reportRef.getFilters()).filter(isUsable);
      diagnostics.report = reportFilters.length;
      collect(SCOPE.report, reportFilters);
    } catch (error) {
      console.warn("[AddVisuals] report.getFilters failed", error);
    }

    try {
      const pageList = await reportRef.getPages();
      const page =
        pageList.find((item) => item.name === selectedPageName) ||
        pageList.find((item) => item.isActive);

      if (page) {
        try {
          const pageFilters = (await page.getFilters()).filter(isUsable);
          diagnostics.page = pageFilters.length;
          collect(SCOPE.page, pageFilters);
        } catch (error) {
          console.warn("[AddVisuals] page.getFilters failed", error);
        }

        const pageVisuals = await page.getVisuals();
        const slicers = pageVisuals.filter((item) => item.type === "slicer");
        diagnostics.slicerCount = slicers.length;

        for (const slicer of slicers) {
          let slicerFilters = [];
          try {
            const state = await slicer.getSlicerState();
            slicerFilters = (state?.filters || []).filter(isUsable);
          } catch (error) {
            console.warn("[AddVisuals] getSlicerState failed", slicer.name, error);
          }

          if (slicerFilters.length === 0) continue;

          diagnostics.slicers += slicerFilters.length;
          collect(SCOPE.slicer, slicerFilters);

          try {
            const exported = await slicer.exportData(models.ExportDataType.Summarized, 10000);
            const members = parseExportedColumn(exported?.data);
            if (members.length > 0) {
              options[filterKeyOf(slicerFilters[0])] = members;
            }
          } catch (error) {
            console.warn("[AddVisuals] exportData failed", slicer.name, error);
          }
        }

        for (const visual of pageVisuals.filter((item) => visualIds.includes(item.name))) {
          try {
            const visualFilters = (await visual.getFilters()).filter(isUsable);
            diagnostics.visual += visualFilters.length;
            collect(SCOPE.visual, visualFilters);
          } catch (error) {
            console.warn("[AddVisuals] visual.getFilters failed", visual.name, error);
          }
        }
      }
    } catch (error) {
      console.warn("[AddVisuals] page walk failed", error);
      setMessage({ type: "danger", text: "Unable to read the current filters." });
    }

    const byTarget = new Map();
    collected.forEach(({ scope, filter }) => {
      const key = filterKeyOf(filter);
      const winner = byTarget.get(key);
      if (!winner || scope >= winner.scope) byTarget.set(key, { scope, filter });
    });
    const filters = [...byTarget.values()].map((entry) => entry.filter);

    filters.forEach((filter) => {
      const key = filterKeyOf(filter);
      if (!options[key]) options[key] = filter.values.map(String);
    });

    console.info("[AddVisuals] captured filters", diagnostics, filters, options);
    return { filters, options };
  };

  const isSameCartRow = (item, row) =>
    item.report_id === row.report_id &&
    item.report_name === row.report_name &&
    item.visual_name === row.visual_name;

  const setCartItemOptions = (row, key, rawText) => {
    const values = rawText
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);

    setCart((prev) =>
      prev.map((item) =>
        isSameCartRow(item, row)
          ? { ...item, filterOptions: { ...item.filterOptions, [key]: values } }
          : item
      )
    );
  };

  const recaptureCartItemFilters = async (row) => {
    const { filters, options } = await captureCurrentFilters([row.visual_name]);
    setCart((prev) =>
      prev.map((item) =>
        isSameCartRow(item, row)
          ? { ...item, filters, filterOptions: options }
          : item
      )
    );
    setMessage({
      type: filters.length ? "success" : "danger",
      text: filters.length
        ? `${filters.length} filter(s) captured for "${row.title || row.visual_name}".`
        : "No filters are currently applied in the report.",
    });
  };

  const clearCartItemFilters = (row) => {
    setCart((prev) =>
      prev.map((item) => (isSameCartRow(item, row) ? { ...item, filters: [], filterOptions: {} } : item))
    );
  };

  const addSelectedToCart = async () => {
    if (!selectedPageName) {
      setMessage({ type: "danger", text: "Select a page first." });
      return;
    }

    if (selectedVisualNames.length === 0) {
      setMessage({ type: "danger", text: "Select at least one visual." });
      return;
    }

    const { filters: capturedFilters, options: capturedOptions } = await captureCurrentFilters(
      selectedVisualNames
    );

    const selectedRows = availableVisuals
      .filter((visual) => selectedVisualNames.includes(visual.id))
      .map((visual) => ({
        report_id: parseInt(report.id, 10),
        report_name: selectedPageName,
        title: visual.title,
        visual_name: visual.id,
        userid: localStorage.getItem("userId") || "",
        user_group_id: null, // No persona association - report-level visual
        is_default: "NO",
        filters: capturedFilters,
        filterOptions: capturedOptions,
        reportLabel: report.reportName,
        pageLabel: selectedPageDisplayName || selectedPageName,
      }));

    const merged = [...cart];
    selectedRows.forEach((row) => {
      const exists = merged.some((item) => isSameCartRow(item, row));
      if (!exists) {
        merged.push(row);
      }
    });

    setCart(merged);
    setSelectedVisualNames([]);
    setMessage({
      type: "success",
      text: `${selectedRows.length} visual(s) added to cart${
        capturedFilters.length ? ` with ${capturedFilters.length} filter(s)` : ""
      }.`,
    });
  };

  const setCartItemDefault = (row, nextDefault) => {
    if (nextDefault === "YES") {
      const currentIsDefault = row.is_default === "YES";
      if (!currentIsDefault && defaultCount >= 2) {
        setMessage({
          type: "danger",
          text: "Maximum 2 visuals can be marked as default.",
        });
        return;
      }
    }

    setCart((prev) =>
      prev.map((item) => {
        const isTarget =
          item.report_id === row.report_id &&
          item.report_name === row.report_name &&
          item.visual_name === row.visual_name;

        if (!isTarget) {
          return item;
        }

        return {
          ...item,
          is_default: nextDefault,
        };
      })
    );

    setMessage({ type: "", text: "" });
  };

  const removeFromCart = (row) => {
    setCart((prev) =>
      prev.filter(
        (item) =>
          !(
            item.report_id === row.report_id &&
            item.report_name === row.report_name &&
            item.visual_name === row.visual_name
          )
      )
    );
  };

  const saveCart = async () => {
    if (cart.length === 0) {
      setMessage({ type: "danger", text: "Cart is empty." });
      return;
    }

    if (defaultCount > 2) {
      setMessage({
        type: "danger",
        text: "Maximum 2 visuals can be marked as default.",
      });
      return;
    }

    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      const response = await apiService.post(apiUrl.aipdAddVisualsToCart(), {
        items: cart.map((item) => ({
          report_id:     item.report_id,
          report_name:   item.report_name,
          title:         item.title,
          visual_name:   item.visual_name,
          userid:        item.userid,
          user_group_id: item.user_group_id, // null for report-level visuals
          is_default:    item.is_default,
          filters:       item.filters || [],
          filterOptions: item.filterOptions || {},
        })),
      });

      setCart([]);
      showToast(
        `Saved. Inserted: ${response.data?.insertedCount || 0}, Updated: ${response.data?.updatedCount || 0}, Skipped: ${response.data?.skippedCount || 0}`
      );
      // Close dialog and reset so next open re-fetches fresh data
      setOpen(false);
      resetState();
    } catch (error) {
      showToast(error?.response?.data?.message || "Unable to save visuals.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <AppButton
        variant="fill"
        fillColor="#7c3aed"
        onClick={openDialog}
        style={{ padding: "4px 10px", fontSize: "11px" }}
      >
        Add Visuals
      </AppButton>

      <Dialog open={open} onClose={closeDialog} maxWidth="lg" fullWidth>
        <DialogTitle style={{ background: "var(--app-button-color)", color: "#fff", fontWeight: 500 }}>
          Add Visuals - {report.reportName}
        </DialogTitle>
        <DialogContent style={{ marginTop: "10px" }}>
          {message.text && <Alert variant={message.type || "info"}>{message.text}</Alert>}

          {savedVisuals.length > 0 && (
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "6px", padding: "10px 14px", marginBottom: "14px", fontSize: "12px" }}>
              <div style={{ fontWeight: 600, color: "#14653a", marginBottom: "6px" }}>
                Already saved visuals ({savedVisuals.length})
              </div>
              {Object.entries(
                savedVisuals.reduce((acc, v) => {
                  const key = v.report_name || "Unknown page";
                  if (!acc[key]) acc[key] = [];
                  acc[key].push(v.title || v.visual_name);
                  return acc;
                }, {})
              ).map(([page, titles]) => (
                <div key={page} style={{ marginBottom: "4px" }}>
                  <span style={{ fontWeight: 500, color: "#166534" }}>{page}:</span>{" "}
                  <span style={{ color: "#15803d" }}>{titles.join(", ")}</span>
                </div>
              ))}
            </div>
          )}

          {loadingReport && (
            <div className="mb-3 text-muted">Loading report...</div>
          )}

          {reportConfig?.embedReport && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Select Page</Form.Label>
                <Form.Select
                  value={selectedPageName}
                  onChange={handlePageChange}
                  disabled={pages.length === 0}
                >
                  <option value="">Select page</option>
                  {pages.map((page) => (
                    <option key={page.name} value={page.name}>
                      {page.displayName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <div
                style={{
                  height: "360px",
                  border: "1px solid #e5e5e5",
                  borderRadius: "6px",
                  overflow: "hidden",
                  marginBottom: "12px",
                }}
              >
                <PowerBIEmbed
                  embedConfig={{
                    type: "report",
                    id: `${reportConfig.embedReport.reportGuid}`,
                    embedUrl: reportConfig.embedReport.url,
                    accessToken: reportConfig.embedReport.embedToken,
                    tokenType: models.TokenType.Embed,
                    viewMode: models.ViewMode.View,
                    pageName: selectedPageName || undefined,
                    settings: {
                      navContentPaneEnabled: false,
                      filterPaneEnabled: true,
                      panes: {
                        filters: {
                          expanded: false,
                          visible: true,
                        },
                      },
                    },
                  }}
                  eventHandlers={new Map([["error", function () {}]])}
                  cssClassName={"report-style-class"}
                  getEmbeddedComponent={handleEmbeddedReport}
                />
              </div>

              <div
                style={{
                  maxHeight: "220px",
                  overflowY: "auto",
                  border: "1px solid #ececec",
                  borderRadius: "6px",
                  padding: "10px",
                  marginBottom: "10px",
                }}
              >
                {availableVisuals.length === 0 && (
                  <span style={{ color: "#666" }}>No visuals found for this page.</span>
                )}

                {availableVisuals.map((visual) => (
                  <div
                    key={visual.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      borderBottom: "1px solid #f3f3f3",
                      padding: "6px 0",
                    }}
                  >
                    <Form.Check
                      type="checkbox"
                      id={`report_visual_${visual.id}`}
                      checked={selectedVisualNames.includes(visual.id)}
                      onChange={() => toggleVisual(visual.id)}
                      label={`${visual.title} (${visual.type})`}
                    />
                  </div>
                ))}
              </div>

              <AppButton variant="fill" onClick={addSelectedToCart} style={{ marginBottom: "14px" }}>
                Add Selected To Cart
              </AppButton>
              <div style={{ fontSize: "12px", color: "#666", marginBottom: "14px" }}>
                Slicer selections and filter-pane filters applied above are saved with the
                visual and replayed wherever it is shown.
              </div>
            </>
          )}

          <div
            style={{
              border: "1px solid #ececec",
              borderRadius: "6px",
              padding: "10px",
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: "8px" }}>
              Cart ({cart.length}) - Default selected: {defaultCount}/2
            </div>

            {cart.length === 0 && <span style={{ color: "#666" }}>No visuals in cart.</span>}

            {cart.map((item, index) => (
              <div
                key={`${item.report_id}_${item.report_name}_${item.visual_name}_${index}`}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid #f3f3f3",
                  padding: "6px 0",
                }}
              >
                <div>
                  <div style={{ fontWeight: 500 }}>{item.title || item.visual_name}</div>
                  <div style={{ fontSize: "12px", color: "#666" }}>
                    {item.reportLabel || "Report"} / {item.pageLabel}
                  </div>
                  <div style={{ marginTop: "8px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "12px", color: "#555" }}>Is Default:</span>
                    <Form.Check
                      inline
                      type="radio"
                      id={`default_yes_${item.report_id}_${item.visual_name}_${index}`}
                      name={`default_${item.report_id}_${item.report_name}_${item.visual_name}_${index}`}
                      label="Yes"
                      checked={item.is_default === "YES"}
                      onChange={() => setCartItemDefault(item, "YES")}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      id={`default_no_${item.report_id}_${item.visual_name}_${index}`}
                      name={`default_${item.report_id}_${item.report_name}_${item.visual_name}_${index}`}
                      label="No"
                      checked={item.is_default !== "YES"}
                      onChange={() => setCartItemDefault(item, "NO")}
                    />
                  </div>
                  <div style={{ marginTop: "6px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "12px", color: "#555" }}>
                      Filters: {(item.filters || []).length}
                    </span>
                    <AppButton
                      variant="secondary"
                      style={{ padding: "2px 8px", fontSize: "11px" }}
                      onClick={() => recaptureCartItemFilters(item)}
                    >
                      Capture current
                    </AppButton>
                    {(item.filters || []).length > 0 && (
                      <AppButton
                        variant="secondary"
                        style={{ padding: "2px 8px", fontSize: "11px" }}
                        onClick={() => clearCartItemFilters(item)}
                      >
                        Clear
                      </AppButton>
                    )}
                  </div>

                  {(item.filters || []).map((filter) => {
                    const key = filterKeyOf(filter);
                    return (
                      <div key={key} style={{ marginTop: "6px", fontSize: "11px", color: "#555" }}>
                        <span style={{ fontWeight: 500 }}>{key}</span> — values a viewer can pick
                        <Form.Control
                          size="sm"
                          style={{ fontSize: "11px", marginTop: "2px" }}
                          placeholder="e.g. 2021, 2022, 2023"
                          value={(item.filterOptions?.[key] || []).join(", ")}
                          onChange={(event) => setCartItemOptions(item, key, event.target.value)}
                        />
                      </div>
                    );
                  })}
                </div>
                <AppButton variant="secondary" onClick={() => removeFromCart(item)}>
                  Remove
                </AppButton>
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions style={{ marginRight: "30px" }}>
          <AppButton variant="secondary" onClick={closeDialog} disabled={saving}>
            Close
          </AppButton>
          <AppButton variant="fill" disabled={saving} onClick={saveCart}>
            {saving ? (
              <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span><span style={{ paddingLeft: "8px" }}>Saving...</span></>
            ) : "Add Visuals to Report"}
          </AppButton>
        </DialogActions>
      </Dialog>
      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast(t => ({ ...t, open: false }))}
      />
    </>
  );
};

export default AddVisualsToReportDialog;
